/**
 * Local mirror and pending-op queue.
 *
 * Every write lands here first and is reflected in the UI immediately; the queue
 * drains to the Worker in the background. Sheets round-trips are ~1s, so the app
 * would feel broken if it waited for them — and this is also what makes logging
 * work with no connection at all.
 */

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { Client, Entry } from '$shared/types';

/**
 * Svelte 5 `$state` values are Proxies, and IndexedDB's structured-clone
 * algorithm refuses to clone a Proxy — writing one straight to a store throws
 * `DataCloneError`. Snapshotting at this boundary means no caller has to
 * remember. (`$state.snapshot` passes plain values through unchanged, so it is
 * safe to apply to everything.)
 *
 * This is why the module is `db.svelte.ts`: runes are only compiled in
 * `.svelte` / `.svelte.ts` files.
 */
function plain<T>(value: T): T {
  return $state.snapshot(value) as T;
}

export type PendingOp =
  | { seq?: number; kind: 'create'; entry: Entry }
  | { seq?: number; kind: 'update'; id: string; clientId: string; patch: Partial<Entry> }
  | { seq?: number; kind: 'delete'; id: string; clientId: string };

interface TimesheetDB extends DBSchema {
  entries: {
    key: string;
    value: Entry;
    indexes: { 'by-client': string };
  };
  queue: {
    key: number;
    value: PendingOp;
  };
  meta: {
    key: string;
    value: unknown;
  };
}

let dbPromise: Promise<IDBPDatabase<TimesheetDB>> | null = null;

function db() {
  dbPromise ??= openDB<TimesheetDB>('timesheet', 1, {
    upgrade(database) {
      const entries = database.createObjectStore('entries', { keyPath: 'id' });
      entries.createIndex('by-client', 'clientId');
      database.createObjectStore('queue', { keyPath: 'seq', autoIncrement: true });
      database.createObjectStore('meta');
    },
  });
  return dbPromise;
}

// ---------------------------------------------------------------------------
// Entries
// ---------------------------------------------------------------------------

export async function readEntries(clientId: string): Promise<Entry[]> {
  const all = await (await db()).getAllFromIndex('entries', 'by-client', clientId);
  return all.sort((a, b) => (a.date === b.date ? 0 : a.date < b.date ? 1 : -1));
}

export async function putEntry(entry: Entry): Promise<void> {
  await (await db()).put('entries', plain(entry));
}

export async function putEntries(entries: Entry[]): Promise<void> {
  const tx = (await db()).transaction('entries', 'readwrite');
  await Promise.all([...entries.map((e) => tx.store.put(plain(e))), tx.done]);
}

export async function removeEntry(id: string): Promise<void> {
  await (await db()).delete('entries', id);
}

/**
 * Replace the mirror for one client with what the server returned, but keep any
 * entry still waiting in the queue — otherwise a refresh mid-sync would make an
 * unsent entry vanish from the screen.
 */
export async function replaceClientEntries(clientId: string, fresh: Entry[]): Promise<void> {
  const pendingIds = new Set(
    (await readQueue())
      .map((op) => (op.kind === 'create' ? op.entry.id : op.id))
      .filter(Boolean),
  );

  const database = await db();
  const tx = database.transaction('entries', 'readwrite');
  const index = tx.store.index('by-client');

  for await (const cursor of index.iterate(clientId)) {
    if (!pendingIds.has(cursor.value.id)) await cursor.delete();
  }
  for (const entry of fresh) {
    if (!pendingIds.has(entry.id)) await tx.store.put(plain(entry));
  }
  await tx.done;
}

// ---------------------------------------------------------------------------
// Queue
// ---------------------------------------------------------------------------

export async function enqueue(op: PendingOp): Promise<void> {
  await (await db()).add('queue', plain(op));
}

export async function readQueue(): Promise<PendingOp[]> {
  return (await db()).getAll('queue');
}

export async function dequeue(seq: number): Promise<void> {
  await (await db()).delete('queue', seq);
}

export async function queueSize(): Promise<number> {
  return (await db()).count('queue');
}

/** Ids with unsent work, so the UI can badge those rows as not yet synced. */
export async function pendingIds(): Promise<Set<string>> {
  const ops = await readQueue();
  return new Set(ops.map((op) => (op.kind === 'create' ? op.entry.id : op.id)));
}

// ---------------------------------------------------------------------------
// Meta (client list cache, timer, last sync)
// ---------------------------------------------------------------------------

export async function getMeta<T>(key: string): Promise<T | undefined> {
  return (await db()).get('meta', key) as Promise<T | undefined>;
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await (await db()).put('meta', plain(value), key);
}

export async function cacheClients(clients: Client[]): Promise<void> {
  await setMeta('clients', clients);
}

export async function readCachedClients(): Promise<Client[]> {
  return (await getMeta<Client[]>('clients')) ?? [];
}
